import type { ActionLog, ModelLog } from '@/types/logs';

import { ExecutedQuery } from '../types';

export function parseQueryLogs(
  actionResult: { agent_actions?: ActionLog[] },
  modelResult: { model_invocations?: ModelLog[] }
): ExecutedQuery[] {
  const queries: ExecutedQuery[] = [];

  // Extract from action logs. The canonical ActionLog identifies the producing
  // agent via `component_name`.
  for ( const log of ( actionResult.agent_actions ?? [] ) ) {
    const input = log.string_input || '';
    if ( /^\s*(SELECT|INSERT|UPDATE|DELETE|CREATE|DROP|ALTER|WITH)\s/i.test( input ) ) {
      queries.push( { sql: input.trim(), timestamp: log.start_time, tool: log.component_name } );
    }
    if ( log.meta_data ) {
      const md = log.meta_data as Record<string, unknown>;
      const sql = ( md.sql || md.query ) as string | undefined;
      if ( sql ) {
        queries.push( { sql, timestamp: log.start_time, tool: log.component_name } );
      }
    }
  }

  // Extract from model logs — tool calls in LLM messages and choices
  for ( const log of ( modelResult.model_invocations ?? [] ) ) {
    // Collect all message-like objects from both messages (input) and choices (output)
    const allMessages: unknown[] = [];

    if ( typeof log.messages === 'string' ) {
      try { allMessages.push( ...JSON.parse( log.messages ) as unknown[] ); } catch { /* skip */ }
    } else if ( Array.isArray( log.messages ) ) {
      allMessages.push( ...log.messages );
    }

    // LLM tool call responses appear in choices[].message
    if ( Array.isArray( log.choices ) ) {
      for ( const choice of log.choices ) {
        if ( choice.message ) allMessages.push( choice.message );
      }
    }

    for ( const msg of allMessages ) {
      const m = msg as Record<string, unknown>;

      // OpenAI format: tool_calls
      if ( Array.isArray( m.tool_calls ) ) {
        for ( const tc of m.tool_calls as Record<string, unknown>[] ) {
          const fn = tc.function as Record<string, unknown> | undefined;
          let args: Record<string, unknown> | null = null;
          if ( typeof fn?.arguments === 'string' ) {
            try { args = JSON.parse( fn.arguments as string ) as Record<string, unknown>; } catch { /* skip */ }
          } else if ( typeof fn?.arguments === 'object' && fn?.arguments ) {
            args = fn.arguments as Record<string, unknown>;
          }
          if ( args && ( args.sql || args.query ) ) {
            queries.push( {
              sql: ( args.sql || args.query ) as string,
              timestamp: log.start_time,
              tool: ( fn?.name as string ) || log.agent_name
            } );
          }
        }
      }

      // Anthropic format: content array with tool_use blocks
      if ( Array.isArray( m.content ) ) {
        for ( const block of m.content as Record<string, unknown>[] ) {
          if ( block.type === 'tool_use' && block.input ) {
            const input = block.input as Record<string, unknown>;
            if ( input.sql || input.query ) {
              queries.push( {
                sql: ( input.sql || input.query ) as string,
                timestamp: log.start_time,
                tool: ( block.name as string ) || log.agent_name
              } );
            }
          }
        }
      }
    }
  }

  // Sort and deduplicate
  queries.sort( ( a, b ) => ( new Date( a.timestamp || 0 ).getTime() || 0 ) - ( new Date( b.timestamp || 0 ).getTime() || 0 ) );
  const seen = new Set<string>();
  return queries.filter( q => {
    const normalized = q.sql.replace( /\s+/g, ' ' ).trim();
    if ( seen.has( normalized ) ) return false;
    seen.add( normalized );
    return true;
  } );
}
